import { type Ref } from 'vue'
import Sortable from 'sortablejs'

export interface DragReorderOptions {
  handle?: string
  dataIdAttr?: string
  animation?: number
  /**
   * 走 SortableJS 自带的鼠标/触摸事件实现,而不是浏览器原生 HTML5 拖放。
   *
   * 原生实现依赖给被拖元素设 `draggable = true`(Sortable.js 内 `dragEl.draggable = true`),
   * 而 Chromium 对 `<tr>` 不响应这种原生拖放,所以**表格行排序必须开启本项**,
   * 否则表现为"手柄拖不动"。ghost 默认留在原容器内(fallbackOnBody 为 false),
   * 因此 `<tr>` 的列宽不会丢失。
   */
  forceFallback?: boolean
  disabled?: () => boolean
  onReorder: (ids: string[]) => void
}

export interface DragReorderHandle {
  sync: () => void
  destroy: () => void
}

export function useDragReorder(
  containerRef: Ref<HTMLElement | null>,
  options: DragReorderOptions
): DragReorderHandle {
  let instance: Sortable | null = null

  function sync() {
    const el = containerRef.value
    if (!el) {
      destroy()
      return
    }

    const isDisabled = options.disabled?.() ?? false
    if (isDisabled) {
      destroy()
      return
    }

    if (!instance) {
      instance = Sortable.create(el, {
        handle: options.handle ?? '.drag-handle',
        dataIdAttr: options.dataIdAttr ?? 'data-id',
        animation: options.animation ?? 150,
        forceFallback: options.forceFallback ?? false,
        ghostClass: 'is-drag-ghost',
        chosenClass: 'is-drag-chosen',
        onEnd: () => {
          if (!instance) return
          const ids = instance.toArray()
          options.onReorder(ids)
        }
      })
    }
  }

  function destroy() {
    if (instance) {
      instance.destroy()
      instance = null
    }
  }

  return { sync, destroy }
}
